<template>
  <v-dialog
    v-model="dialogVisible"
    max-width="760px"
    scrollable
    :fullscreen="$vuetify.breakpoint.xsOnly"
    transition="dialog-bottom-transition"
  >
    <v-card class="timeline-card">
      <!-- Header -->
      <div class="tl-head">
        <div class="tl-head-main">
          <div class="tl-head-icon">
            <v-icon color="white">mdi-timeline-clock-outline</v-icon>
          </div>
          <div>
            <div class="tl-head-title">Application timeline</div>
            <div class="tl-head-subtitle">
              <template v-if="loading">Loading history…</template>
              <template v-else>{{ records.length }} step{{ records.length === 1 ? "" : "s" }} recorded</template>
            </div>
          </div>
        </div>
        <v-btn icon dark aria-label="Close" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <!-- Application summary -->
      <div class="tl-summary">
        <div class="tl-summary-main">
          <span class="app-no">{{ appNo }}</span>
          <div v-if="campName" class="tl-summary-text">
            <div class="tl-camp">
              <v-icon x-small color="#13736f">mdi-pine-tree</v-icon>
              {{ campName }}
            </div>
            <div v-if="applicantName" class="tl-applicant">Applicant · {{ applicantName }}</div>
          </div>
        </div>
        <div v-if="currentHandler" class="tl-handler">
          <span class="tl-handler-dot"></span>
          {{ currentHandler }}
        </div>
      </div>

      <!-- Body -->
      <v-card-text class="tl-body">
        <div v-if="loading" class="tl-state">
          <v-progress-circular indeterminate color="#13736f" size="44" width="4"></v-progress-circular>
          <div>Loading timeline records…</div>
        </div>

        <div v-else-if="records && records.length > 0">
          <!-- The path at a glance: each stop once -->
          <div class="tl-path" aria-label="Path so far">
            <template v-for="(stop, i) in stops">
              <v-icon v-if="i > 0" :key="'arrow' + i" small color="#94a3b8">mdi-chevron-right</v-icon>
              <span
                :key="'chip' + i"
                class="tl-path-chip"
                :class="{ 'tl-path-chip--current': i === stops.length - 1 }"
              >
                {{ stopRole(stop) }}
              </span>
            </template>
          </div>

          <!-- One entry per stop: who it reached and what they did with it -->
          <ol class="tl-list">
            <li
              v-for="(stop, i) in stops"
              :key="i"
              class="tl-step"
              :class="{ 'tl-step--latest': i === stops.length - 1 }"
            >
              <div class="tl-marker">
                <span class="tl-dot" :class="'tl-dot--' + stopTone(stop)">
                  <v-icon small>{{ roleIcon(stop) }}</v-icon>
                </span>
              </div>

              <div class="tl-content">
                <div class="tl-step-head">
                  <div class="tl-who">
                    <span class="tl-role" :class="'tl-role--' + stopTone(stop)">{{ stopRole(stop) }}</span>
                    <span v-if="isApplicant(stop.person)" class="tl-person-name">{{ personName(stop.person) }}</span>
                    <span v-else-if="stop.person && stop.person.officeName" class="tl-person-name">
                      {{ stop.person.officeName }}
                    </span>
                  </div>
                  <span v-if="i === stops.length - 1" class="tl-latest">{{ isClosed ? "Latest" : "Currently here" }}</span>
                </div>

                <!-- What this person did -->
                <div v-for="(act, a) in stop.actions" :key="a" class="tl-action">
                  <div class="tl-arrival">
                    <v-icon x-small color="#13736f">{{ act.to ? "mdi-send-outline" : "mdi-flag-checkered" }}</v-icon>
                    <span>
                      {{ act.label }}
                      <template v-if="act.to">
                        to <b>{{ isApplicant(act.to) ? "Applicant" : personRole(act.to) || personName(act.to) }}</b>
                      </template>
                    </span>
                    <span v-if="act.by && !isApplicant(stop.person)" class="tl-by">
                      <v-icon x-small color="#13736f">mdi-account-check-outline</v-icon>
                      by {{ personName(act.by) }}
                    </span>
                    <span v-if="act.at" class="tl-time">&middot; {{ formatTimestamp(act.at) }}</span>
                  </div>
                  <div v-if="act.remarks" class="tl-remarks">
                    <v-icon x-small color="#b45309">mdi-message-text-outline</v-icon>
                    <div>
                      <span class="tl-remarks-label">Remarks</span>
                      {{ act.remarks }}
                    </div>
                  </div>
                </div>

                <!-- Hasn't acted yet -->
                <div v-if="!stop.actions.length" class="tl-arrival tl-arrival--waiting">
                  <v-icon x-small color="#94a3b8">mdi-inbox-arrow-down-outline</v-icon>
                  <span>Received</span>
                  <span v-if="stop.receivedAt" class="tl-time">&middot; {{ formatTimestamp(stop.receivedAt) }}</span>
                  <span v-if="!isClosed" class="tl-waiting">Awaiting action</span>
                </div>
              </div>
            </li>
          </ol>
        </div>

        <div v-else class="tl-state">
          <v-icon size="48" color="#cbd5e1">mdi-timeline-remove-outline</v-icon>
          <div>No timeline records found for this application.</div>
        </div>
      </v-card-text>

      <!-- Footer -->
      <div class="tl-footer">
        <v-btn
          v-if="appId"
          depressed
          class="btn-act btn-act--info"
          @click="goToRecordsPage"
        >
          <v-icon small left>mdi-open-in-new</v-icon>
          Full records view
        </v-btn>
        <v-spacer v-else></v-spacer>
        <v-btn depressed class="btn-act btn-act--primary px-6" @click="close">
          Close
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from "axios";
import moment from "moment";
import { getRoleName, getCurrentlyWith, withHolderFromRecords } from "@/utils/applicationHelper";

export default {
  name: "ApplicationTimelineDialog",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    appId: {
      type: String,
      default: null,
    },
    application: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      loading: false,
      records: [],
    };
  },
  computed: {
    // Each person the application has reached, once and in order. Every
    // timeline record is shown on the stop of the person who did it (the
    // sender), so remarks sit with whoever wrote them.
    stops() {
      const recs = this.records || [];
      if (!recs.length) return [];
      const stops = [{ person: recs[0].forwardFrom, actions: [], receivedAt: null }];
      recs.forEach((r) => {
        const at = r.forwardDate || r.created_at;
        stops[stops.length - 1].actions.push({
          by: r.forwardFrom,
          label: r.processDone || "Forwarded",
          to: r.forwardTo || null,
          at,
          remarks: r.remarks,
        });
        if (r.forwardTo) stops.push({ person: r.forwardTo, actions: [], receivedAt: at });
      });
      return stops;
    },
    isClosed() {
      return ["Sanctioned", "Cancelled", "Rejected"].includes(this.currentHandler);
    },
    dialogVisible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    appNo() {
      if (this.application) {
        return (
          this.application.applicationNo ||
          this.application.applicationId ||
          this.application._id ||
          this.appId ||
          "N/A"
        );
      }
      return this.appId || "N/A";
    },
    campName() {
      if (this.application && this.application.campid) {
        return this.application.campid.campName || "";
      }
      return "";
    },
    applicantName() {
      return (this.application && this.application.applicantname) || "";
    },
    currentHandler() {
      return getCurrentlyWith(withHolderFromRecords(this.application, this.records));
    },
  },
  watch: {
    dialogVisible(val) {
      if (val && this.appId) {
        this.fetchTimeline();
      } else if (!val) {
        this.records = [];
      }
    },
    appId(newVal) {
      if (this.dialogVisible && newVal) {
        this.fetchTimeline();
      }
    },
  },
  methods: {
    close() {
      this.dialogVisible = false;
    },
    goToRecordsPage() {
      if (this.appId) {
        this.$router.push(`/records?id=${this.appId}`);
        this.close();
      }
    },
    getStepColor(index, total) {
      if (index === total - 1) return "#13736f"; // latest step
      if (index === 0) return "#283e51"; // start
      return "#546e7a";
    },
    formatTimestamp(dateString) {
      if (!dateString) return "Nill";
      return moment(dateString).format("DD-MM-YYYY, hh:mm A");
    },
    formatUserLabel(userObj) {
      if (!userObj) return "Nill";
      if (typeof userObj === "string") return userObj;
      const role = getRoleName(userObj.uType);
      const name = userObj.username || userObj.name || "";
      if (name && role && role !== "Official") {
        return `${name} (${role})`;
      } else if (role && role !== "Official") {
        return role;
      }
      return name || "Official";
    },
    personName(userObj) {
      if (!userObj) return "Nill";
      if (typeof userObj === "string") return userObj;
      return userObj.username || userObj.name || getRoleName(userObj.uType);
    },
    personRole(userObj) {
      if (!userObj || typeof userObj === "string") return "";
      const role = getRoleName(userObj.uType);
      return role !== "Official" ? role : "";
    },
    isApplicant(userObj) {
      return !!userObj && typeof userObj === "object" && Number(userObj.uType) === 772;
    },
    roleIcon(stop) {
      const icons = {
        Applicant: "mdi-account-outline",
        "DFO Clerk": "mdi-file-document-edit-outline",
        "DFO MH": "mdi-account-tie-outline",
        DFO: "mdi-pine-tree",
        "CWW Clerk": "mdi-briefcase-outline",
        "CWW MH": "mdi-account-tie-hat-outline",
        "Super Admin": "mdi-shield-crown-outline",
      };
      return icons[this.personRole(stop.person)] || "mdi-account-circle-outline";
    },
    stopRole(stop) {
      if (this.isApplicant(stop.person)) return "Applicant";
      return this.personRole(stop.person) || "Officer";
    },
    stopTone(stop) {
      const role = this.personRole(stop.person);
      if (role === "Applicant") return "applicant";
      if (role === "DFO" || role === "Super Admin") return "decider";
      return "staff";
    },
    personInitial(userObj) {
      return this.personName(userObj).charAt(0).toUpperCase() || "?";
    },
    fetchTimeline() {
      if (!this.appId) return;
      this.loading = true;
      axios({
        method: "POST",
        url: "/records/application",
        headers: {
          token: localStorage.getItem("token"),
        },
        data: {
          appid: this.appId,
        },
      })
        .then((response) => {
          this.loading = false;
          if (response.data && response.data.status && Array.isArray(response.data.data)) {
            this.records = response.data.data;
          } else {
            this.records = [];
          }
        })
        .catch((err) => {
          this.loading = false;
          console.error("Error fetching application timeline:", err);
          this.records = [];
        });
    },
  },
};
</script>

<style scoped>
.timeline-card {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ---------- Header ---------- */
.tl-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 16px 18px 24px;
  background: linear-gradient(120deg, #0c4f4c 0%, #13736f 60%, #1b857f 100%);
  color: #ffffff;
}

.tl-head-main {
  display: flex;
  align-items: center;
  gap: 14px;
}

.tl-head-icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.16);
}

.tl-head-title {
  font-size: 18px;
  font-weight: 800;
  line-height: 1.25;
}

.tl-head-subtitle {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.75);
}

/* ---------- Summary ---------- */
.tl-summary {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 24px;
  background: #f4f9f8;
  border-bottom: 1px solid #e6edf0;
}

.tl-summary-main {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.tl-summary-text {
  min-width: 0;
}

.tl-camp {
  font-size: 14px;
  font-weight: 800;
  color: #0f172a;
  text-transform: capitalize;
}

.tl-applicant {
  font-size: 12.5px;
  color: #64748b;
}

.tl-handler {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 999px;
  background: #ffffff;
  border: 1px solid #d8e6e4;
  font-size: 12.5px;
  font-weight: 700;
  color: #0c4f4c;
}

.tl-handler-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #f59e0b;
  box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.2);
}

/* ---------- Body ---------- */
.tl-body {
  max-height: 540px;
  padding: 24px !important;
  background: #fbfdfd;
}

.tl-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 48px 16px;
  color: #64748b;
  font-weight: 600;
  text-align: center;
}

.tl-path {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 2px;
  margin-bottom: 18px;
  padding: 10px 12px;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid #e6edf0;
}

.tl-path-chip {
  padding: 3px 10px;
  border-radius: 999px;
  background: #f1f5f9;
  color: #334155;
  font-size: 12.5px;
  font-weight: 700;
  white-space: nowrap;
}

.tl-path-chip--current {
  background: #13736f;
  color: #ffffff;
}

.tl-list {
  position: relative;
  margin: 0;
  padding: 0;
  list-style: none;
}

.tl-step {
  position: relative;
  display: flex;
  gap: 14px;
  padding-bottom: 14px;
}

.tl-step:last-child {
  padding-bottom: 0;
}

/* rail between stops */
.tl-step::before {
  content: "";
  position: absolute;
  left: 17px;
  top: 38px;
  bottom: 0;
  width: 2px;
  background: #d8e6e4;
}

.tl-step:last-child::before {
  display: none;
}

.tl-marker {
  flex-shrink: 0;
  width: 36px;
  display: flex;
  justify-content: center;
}

.tl-dot {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 800;
  border: 2px solid #ffffff;
  box-shadow: 0 0 0 1px #d8e6e4;
}

.tl-dot--applicant {
  background: #dbeafe;
  color: #1d4ed8;
}

.tl-dot--staff {
  background: rgba(19, 115, 111, 0.12);
  color: #13736f;
}

.tl-dot--decider {
  background: #fef3c7;
  color: #b45309;
}

.tl-dot--event {
  background: #13736f;
}

.tl-step--latest .tl-dot {
  box-shadow: 0 0 0 3px rgba(19, 115, 111, 0.35);
}

.tl-content {
  flex: 1;
  min-width: 0;
  padding: 12px 14px;
  border-radius: 14px;
  background: #ffffff;
  border: 1px solid #e6edf0;
}

.tl-step--latest .tl-content {
  border-color: rgba(19, 115, 111, 0.4);
  box-shadow: 0 8px 22px rgba(19, 115, 111, 0.1);
}

.tl-step-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 6px 10px;
}

.tl-who {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;
  min-width: 0;
}

.tl-role {
  padding: 1px 8px;
  border-radius: 6px;
  font-size: 11.5px;
  font-weight: 800;
  background: rgba(19, 115, 111, 0.1);
  color: #0c4f4c;
}

.tl-role--applicant {
  background: #dbeafe;
  color: #1d4ed8;
}

.tl-role--decider {
  background: #fef3c7;
  color: #92400e;
}

.tl-person-name {
  font-size: 15px;
  font-weight: 800;
  color: #0f172a;
  word-break: break-word;
}

.tl-office {
  font-size: 12.5px;
  color: #64748b;
}

.tl-latest {
  padding: 1px 8px;
  border-radius: 999px;
  background: rgba(19, 115, 111, 0.1);
  color: #13736f;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
}

.tl-arrival {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 6px;
  margin-top: 6px;
  font-size: 13px;
  color: #475569;
}

.tl-arrival b {
  color: #0f172a;
}

.tl-time {
  color: #64748b;
  white-space: nowrap;
}

.tl-action + .tl-action {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed #e6edf0;
}

.tl-arrival--waiting {
  color: #64748b;
}

.tl-waiting {
  padding: 1px 8px;
  border-radius: 999px;
  background: #fef3c7;
  color: #92400e;
  font-size: 11.5px;
  font-weight: 800;
}

.tl-by {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 1px 8px;
  border-radius: 999px;
  background: rgba(19, 115, 111, 0.08);
  color: #0c4f4c;
  font-size: 12px;
  font-weight: 700;
}

.tl-dot .v-icon {
  color: inherit !important;
}

.tl-remarks {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 10px;
  padding: 8px 12px;
  border-radius: 10px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  font-size: 13px;
  color: #451a03;
  line-height: 1.45;
}

.tl-remarks .v-icon {
  margin-top: 3px;
}

.tl-remarks-label {
  font-weight: 800;
  color: #b45309;
  margin-right: 4px;
}

/* ---------- Footer ---------- */
.tl-footer {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid #e6edf0;
  background: #ffffff;
}

/* ---------- Phone ---------- */
@media (max-width: 600px) {
  .tl-head {
    padding: 14px 8px 14px 16px;
  }

  .tl-summary {
    padding: 12px 16px;
  }

  .tl-body {
    max-height: none;
    padding: 16px 12px !important;
  }

  .tl-step {
    gap: 10px;
  }

  .tl-marker,
  .tl-dot {
    width: 30px;
  }

  .tl-dot {
    height: 30px;
    font-size: 12px;
  }

  .tl-step::before {
    left: 14px;
    top: 32px;
  }

  .tl-content {
    padding: 10px 12px;
  }

  .tl-path {
    flex-wrap: nowrap;
    overflow-x: auto;
  }

  .tl-footer {
    padding: 10px 12px calc(10px + env(safe-area-inset-bottom));
  }
}
</style>
